import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@volca/ui/components/ui/select";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import type { QuickbooksAccount } from "@/business/quickbooks";
import { useGetQuickbooksAccounts } from "@/business/quickbooks-query";
import { quickbooksAccountLabel } from "@/business/quickbooks-utils";
import { QUICKBOOKS_BUSINESS_DEFAULT } from "./quickbooks-labels";

const BUSINESS_DEFAULT_VALUE = "business_default";

/**
 * A vendor's own expense account, or the business default (null). The
 * current account stays listed even if QuickBooks made it inactive. Without
 * `allowDefault` (a bill's account) there is no "Business default" choice.
 */
export const QuickbooksExpenseAccountSelect = ({
  id,
  businessId,
  value,
  onChange,
  disabled,
  allowDefault = true,
}: {
  id: string;
  businessId: string | null;
  value: QuickbooksAccount | null;
  onChange: (accountId: string | null) => void;
  disabled?: boolean;
  allowDefault?: boolean;
}) => {
  const {
    data: accounts,
    isPending,
    isError,
  } = useGetQuickbooksAccounts(businessId);

  if (isPending) {
    return (
      <Stack direction="row" gap={2} align="center" role="status">
        <Spinner layout="centered" size="sm" />
        <Text tone="muted">Loading accounts...</Text>
      </Stack>
    );
  }

  if (isError) {
    return (
      <Text tone="destructive" role="alert">
        Couldn’t load your QuickBooks accounts. Please try again.
      </Text>
    );
  }

  const options =
    value && !accounts.some((account) => account.id === value.id)
      ? [value, ...accounts]
      : accounts;

  const items = [
    ...(allowDefault
      ? [{ value: BUSINESS_DEFAULT_VALUE, label: QUICKBOOKS_BUSINESS_DEFAULT }]
      : []),
    ...options.map((account) => ({
      value: account.id,
      label: quickbooksAccountLabel(account),
    })),
  ];

  return (
    <Select
      value={value?.id ?? (allowDefault ? BUSINESS_DEFAULT_VALUE : null)}
      items={items}
      onValueChange={(next) => {
        if (next) onChange(next === BUSINESS_DEFAULT_VALUE ? null : next);
      }}
      disabled={disabled}
    >
      <SelectTrigger id={id}>
        <SelectValue placeholder="Choose an account" />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {items.map((item) => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};
